import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import GuaranteeBadge from "@/components/GuaranteeBadge";
import usePageMeta from "@/lib/usePageMeta";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6 },
};

export default function Tarifs() {
  const { t } = useTranslation();
  const [yearly, setYearly] = useState(false);
  usePageMeta("pricing");

  const monthly_features = ["f_cash", "f_invoices", "f_vat", "f_ocr_m", "f_ai_m", "f_fiduciary", "f_monthly", "f_support_m"];
  const yearly_features = ["f_cash", "f_invoices", "f_vat", "f_ocr_y", "f_ai_y", "f_fiduciary", "f_monthly", "f_support_y"];

  return (
    <div className="min-h-screen bg-bg text-[var(--text)]">
      <PublicHeader />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-8 text-center">
        <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-secondary-c bg-secondary-c/10 px-3 py-1 rounded-full">
          <Sparkles className="w-3.5 h-3.5" /> {t("pricing_page.badge")}
        </motion.span>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="font-display text-4xl sm:text-5xl md:text-6xl mt-4 leading-tight">
          {t("pricing.title")} <span className="font-serif-italic">{t("pricing.title_accent")}</span> {t("pricing.title_end")}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-4 text-text-secondary text-base sm:text-lg max-w-xl mx-auto">
          {t("pricing_page.subtitle")}
        </motion.p>
      </section>

      {/* Pricing card */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-10">
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-1 p-1 bg-surface-muted rounded-full border border-c" data-testid="pricing-toggle">
            <button onClick={() => setYearly(false)} data-testid="pricing-monthly-btn" className={`px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-full transition-all ${!yearly ? "bg-white shadow text-[var(--text)]" : "text-text-secondary"}`}>{t("pricing.monthly")}</button>
            <button onClick={() => setYearly(true)} data-testid="pricing-yearly-btn" className={`px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-full transition-all inline-flex items-center gap-1.5 ${yearly ? "bg-white shadow text-[var(--text)]" : "text-text-secondary"}`}>
              {t("pricing.yearly")} <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${yearly ? "bg-secondary-c text-white" : "bg-secondary-c/20 text-secondary-c"}`}>{t("pricing.yearly_off")}</span>
            </button>
          </div>
        </div>

        <motion.div {...fadeUp} className="mt-8 card-c max-w-md mx-auto relative" style={{ borderColor: "var(--secondary)", borderWidth: 2 }} data-testid="pricing-card">
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary-c text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">{t("pricing.recommended")}</span>
          <p className="text-sm font-medium text-secondary-c">{yearly ? t("pricing.plan_yearly") : t("pricing.plan_monthly")}</p>
          <div className="mt-2 flex items-baseline gap-2">
            <AnimatePresence mode="wait">
              <motion.p key={yearly ? "y" : "m"} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }} className="font-display text-5xl sm:text-6xl" data-testid="pricing-amount">
                {yearly ? "1'290" : "129"}
              </motion.p>
            </AnimatePresence>
            <span className="text-base text-text-secondary">{yearly ? t("pricing.per_year_unit") : t("pricing.per_month_unit")}</span>
          </div>
          {yearly && <p className="text-xs text-secondary-c mt-1">{t("pricing.yearly_note")}</p>}
          <ul className="mt-5 space-y-2 text-sm">
            {(yearly ? yearly_features : monthly_features).map((k) => (
              <li key={k} className="flex items-start gap-2"><Check className="w-4 h-4 text-primary-c mt-0.5 shrink-0" /><span>{t(`pricing.${k}`)}</span></li>
            ))}
          </ul>
          <Link to="/register" data-testid="pricing-cta" className="btn-primary inline-flex items-center justify-center gap-2 mt-6 w-full text-center group">
            {t("pricing.cta")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <p className="text-xs text-text-secondary mt-3 text-center">{t("pricing.small")}</p>
          <p className="text-[11px] text-text-secondary mt-1 text-center" data-testid="pricing-net-note">{t("pricing.net_note")}</p>
        </motion.div>
      </section>

      {/* Ambassador offer highlight */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <motion.div {...fadeUp} className="rounded-3xl p-6 sm:p-8 border-2 border-secondary-c/30 bg-gradient-to-br from-[#FFF8EB] to-white text-center">
          <span className="text-2xl">🎁</span>
          <h3 className="font-display text-2xl mt-2">{t("pricing_page.ambassador_title")}</h3>
          <p className="mt-2 text-sm text-text-secondary max-w-lg mx-auto">{t("pricing_page.ambassador_desc")}</p>
          <p className="mt-3 text-xs">{t("pricing_page.ambassador_code")} <code className="px-2 py-0.5 bg-secondary-c/10 text-secondary-c font-bold rounded">AMBASSADEUR2026</code></p>
          <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-primary-c text-white rounded-full text-xs font-bold">
            {t("pricing_page.ambassador_lifetime")}
          </div>
          <Link to="/register?promo=AMBASSADEUR2026" data-testid="ambassador-cta" className="btn-primary inline-flex items-center gap-2 mt-4 group">
            {t("pricing_page.ambassador_cta")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </section>

      <GuaranteeBadge />

      {/* Mini-FAQ pricing */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl text-center">{t("pricing_page.faq_title")}</motion.h2>
        <div className="mt-6 space-y-3">
          {["q1", "q2", "q3"].map((k) => (
            <motion.div key={k} {...fadeUp} className="card-c">
              <p className="font-semibold text-base">{t(`pricing_page.faq_${k}`)}</p>
              <p className="text-sm text-text-secondary mt-2">{t(`pricing_page.faq_${k}_a`)}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-center mt-6 text-sm text-text-secondary">
          <Link to="/faq" className="text-primary-c font-semibold underline">{t("pricing_page.see_all_faq")}</Link>
        </p>
      </section>

      <PublicFooter />
    </div>
  );
}
